/**
 * =============================================================================
 * USE SESSION HOOK
 * =============================================================================
 *
 * Hook for accessing admin session context.
 * Provides token, storeHash, and authentication state.
 *
 * ARCHITECTURE REFERENCE: Section 10 - Admin Dashboard
 * PHASE_0B_SPEC.md: Task 2.4.1
 *
 * =============================================================================
 */

import { useContext } from 'react';
import { SessionContext } from '../providers/SessionProvider';
import type { SessionContextValue } from '../providers/SessionProvider';

/**
 * Hook to access the current session.
 *
 * Returns the session context containing:
 * - `token`: The BigCommerce signed_payload_jwt (for API requests)
 * - `storeHash`: The store hash extracted from the JWT
 * - `isAuthenticated`: Whether a valid session exists
 *
 * @example
 * ```tsx
 * function QuestionQueue() {
 *   const { token, storeHash, isAuthenticated } = useSession();
 *
 *   if (!isAuthenticated) {
 *     return <div>Please log in through BigCommerce</div>;
 *   }
 *
 *   // Use token in API requests
 *   const fetchQuestions = async () => {
 *     const response = await fetch('/api/admin/questions', {
 *       headers: {
 *         'Authorization': `Bearer ${token}`,
 *       },
 *     });
 *     // ...
 *   };
 *
 *   return <div>Store: {storeHash}</div>;
 * }
 * ```
 *
 * @returns Session context value with token, storeHash, and isAuthenticated
 */
export function useSession(): SessionContextValue {
  return useContext(SessionContext);
}
